import { useState } from "react";
import { BREAKPOINTS, VIEWPORTS } from "web/react/constants";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import { useWindowWidth } from "web/react/hooks/use-window-width/use-window-width";

interface DomViewport {
    isDomLoaded: boolean;
    isMobileViewport: boolean;
    isTabletViewport: boolean;
    isDesktopViewport: boolean;
}

/**
 * Hook that keeps track of the browser viewport once the DOM has loaded
 *
 * Each viewport check requires that the DOM has been loaded, therefore we should always
 * check these values in a truthy way, not falsey. This way we can be sure that the
 * check is false because the viewport does not match, not because the DOM is yet
 * to load (i.e. we are on the server)
 *
 * ❌ Incorrect:
 *     if (!isMobileViewport) {}
 *
 * ✅ Correct:
 *     if (isDesktopViewport || isTabletViewport) {}
 */
export function useDomViewport(): DomViewport {
    const [isDomLoaded, setIsDomLoaded] = useState(false);
    const windowWidth = useWindowWidth();

    useIsomorphicLayoutEffect(() => {
        setIsDomLoaded(true);
    }, []);

    function getViewport(): number {
        if (windowWidth >= BREAKPOINTS.Large) {
            return VIEWPORTS.Desktop;
        } else if (windowWidth >= BREAKPOINTS.Medium) {
            return VIEWPORTS.Tablet;
        }
        return VIEWPORTS.Mobile;
    }

    const viewport = getViewport();
    const isMobileViewport = isDomLoaded && viewport === VIEWPORTS.Mobile;
    const isTabletViewport = isDomLoaded && viewport === VIEWPORTS.Tablet;
    const isDesktopViewport = isDomLoaded && viewport === VIEWPORTS.Desktop;

    return { isDomLoaded, isMobileViewport, isTabletViewport, isDesktopViewport };
}
